import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { imageUrl } from '../lib/images';

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesAccordion, setMobileServicesAccordion] = useState(false);
  const dropdownRef = useRef(null);
  const leaveTimeoutRef = useRef(null);
  const location = useLocation();

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesAccordion(false);
  }, [location.pathname]);

  // Click outside to close desktop dropdown
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const serviceLinks = [
    { title: 'Reparation af vaskemaskiner', href: '/ydelser/vaskemaskiner', desc: 'Fejlfinding og reparation på alle mærker' },
    { title: 'Reparation af opvaskemaskiner', href: '/ydelser/opvaskemaskiner', desc: 'Integrerede og fritstående maskiner' },
    { title: 'Reparation af tørretumblere', href: '/ydelser/toerretumblere', desc: 'Kondens-, aftræks- og varmepumpetumblere' },
    { title: 'Reparation af køleskabe og frysere', href: '/ydelser/koeleskabe-frysere', desc: 'Kølekomponenter, termostater og pakninger' },
    { title: 'Reparation af ovne og komfurer', href: '/ydelser/ovne-komfurer', desc: 'Indbygningsovne, induktion og plader' },
    { title: 'Serviceeftersyn og vedligehold', href: '/ydelser/serviceeftersyn', desc: 'Forebyggende eftersyn med servicefradrag' },
  ];

  const isServicesActive = location.pathname.startsWith('/ydelser') || location.pathname === '/service-priser';

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200 transition-all">
      {/* Main navigation bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between min-h-20 sm:min-h-24 py-2">
          {/* Logo & Company Name */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={imageUrl('/_assets/imgi-1-ma-standard-e42lzl.jpg', 'height=220,format=auto')}
              alt="MA Hvidevareservice logo"
              className="h-12 sm:h-16 md:h-24 w-auto object-contain rounded-none p-0"
            />
          </Link>

          {/* Desktop Navigation - EXACT REQUIREMENT: Only visible on screens >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-8" aria-label="Hovednavigation">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-sm tracking-tight transition-colors py-2 ${
                  isActive
                    ? 'text-sky-600 font-bold'
                    : 'text-slate-700 hover:text-sky-600 font-medium'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Dropdown for Ydelser */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={() => {
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current);
                  leaveTimeoutRef.current = null;
                }
                setServicesDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current);
                }
                leaveTimeoutRef.current = setTimeout(() => {
                  setServicesDropdownOpen(false);
                }, 200);
              }}
            >
              <Link
                to="/service-priser"
                onClick={() => {
                  if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
                  setServicesDropdownOpen(false);
                }}
                className={`text-sm tracking-tight transition-colors py-2 flex items-center gap-1.5 cursor-pointer ${
                  isServicesActive
                    ? 'text-sky-600 font-bold'
                    : 'text-slate-700 hover:text-sky-600 font-medium'
                }`}
              >
                <span>Ydelser</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    servicesDropdownOpen ? 'rotate-180 text-sky-600' : 'text-slate-400'
                  }`}
                  aria-hidden="true"
                />
              </Link>

              {servicesDropdownOpen && (
                <div
                  className="absolute left-0 top-full pt-1.5 w-[32rem] z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  onMouseEnter={() => {
                    if (leaveTimeoutRef.current) {
                      clearTimeout(leaveTimeoutRef.current);
                      leaveTimeoutRef.current = null;
                    }
                  }}
                  onMouseLeave={() => {
                    if (leaveTimeoutRef.current) {
                      clearTimeout(leaveTimeoutRef.current);
                    }
                    leaveTimeoutRef.current = setTimeout(() => {
                      setServicesDropdownOpen(false);
                    }, 200);
                  }}
                >
                  {/* Invisible bridge to prevent any mouseout deadzone */}
                  <div className="absolute -top-3 left-0 right-0 h-3 pointer-events-auto" />
                  
                  <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-2.5">
                    <div className="grid grid-cols-2 gap-1.5">
                      {serviceLinks.map((item) => {
                        const isCurrent = location.pathname === item.href;
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => {
                              if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
                              setServicesDropdownOpen(false);
                            }}
                            className={`px-3 py-2 rounded-lg transition-colors group flex items-center justify-between gap-2 ${
                              isCurrent ? 'bg-sky-50 text-sky-600' : 'hover:bg-slate-50'
                            }`}
                          >
                            <span className={`text-sm tracking-tight transition-colors ${
                              isCurrent ? 'font-bold text-sky-600' : 'font-semibold text-slate-800 group-hover:text-sky-600'
                            }`}>
                              {item.title}
                            </span>
                            <i className={`fa-solid fa-chevron-right text-[10px] transition-colors shrink-0 ${
                              isCurrent ? 'text-sky-600' : 'text-slate-300 group-hover:text-sky-600'
                            }`} aria-hidden="true" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/om-mig"
              className={({ isActive }) =>
                `text-sm tracking-tight transition-colors py-2 ${
                  isActive
                    ? 'text-sky-600 font-bold'
                    : 'text-slate-700 hover:text-sky-600 font-medium'
                }`
              }
            >
              Om mig
            </NavLink>
          </nav>

          {/* Desktop Right CTA Area */}
          <div className="hidden min-[1200px]:flex items-center gap-5">
            <a
              href="tel:69306445"
              className="text-slate-900 hover:text-sky-600 transition-colors flex items-center gap-2 text-sm font-bold tracking-tight whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-600 text-sm" aria-hidden="true" />
              <span>69 30 64 45</span>
            </a>

            <Link
              to="/kontakt"
              className="px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-lg tracking-tight transition-colors shadow-sm whitespace-nowrap"
            >
              Kontakt
            </Link>
          </div>

          {/* Mobile/Tablet Controls - EXACT REQUIREMENT: Visible on screens < 1200px */}
          <div className="flex min-[1200px]:hidden items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
              className="p-2 text-slate-800 flex items-center justify-center cursor-pointer outline-none focus:outline-none focus:ring-0 focus:bg-transparent active:bg-transparent"
            >
              {mobileMenuOpen ? (
                <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
              ) : (
                <i className="fa-solid fa-bars text-2xl" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet slide-down Navigation Drawer */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-slate-200 shadow-2xl px-4 py-6 max-h-[calc(100vh-80px)] overflow-y-auto animate-in slide-in-from-top-4 duration-200">
          <nav className="flex flex-col space-y-1" aria-label="Mobil navigation">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-3 rounded-lg text-base transition-colors flex items-center justify-between ${
                  isActive ? 'text-sky-600 font-bold' : 'text-slate-800 hover:text-sky-600 font-medium'
                }`
              }
            >
              <span>Forside</span>
              <i className="fa-solid fa-chevron-right text-xs text-slate-300" aria-hidden="true" />
            </NavLink>

            {/* Mobile Accordion for Ydelser */}
            <div className="rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileServicesAccordion((prev) => !prev)}
                aria-expanded={mobileServicesAccordion}
                className={`w-full px-4 py-3 rounded-lg text-base transition-colors flex items-center justify-between cursor-pointer ${
                  isServicesActive ? 'text-sky-600 font-bold' : 'text-slate-800 hover:text-sky-600 font-medium'
                }`}
              >
                <span>Ydelser</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    mobileServicesAccordion ? 'rotate-180 text-sky-600' : 'text-slate-400'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {mobileServicesAccordion && (
                <div className="pl-4 pr-2 py-1 space-y-1 bg-slate-50/70 rounded-lg my-1">
                  <NavLink
                    to="/service-priser"
                    end
                    className={({ isActive }) =>
                      `block px-3 py-2 text-sm rounded transition-colors ${
                        isActive || location.pathname === '/ydelser'
                          ? 'text-sky-600 font-bold bg-white shadow-xs'
                          : 'text-slate-700 hover:text-sky-600 hover:bg-white font-medium'
                      }`
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span>Alle ydelser</span>
                      <i className="fa-solid fa-arrow-right text-xs opacity-60" aria-hidden="true" />
                    </div>
                  </NavLink>
                  {serviceLinks.map((item) => {
                    const isCurrent = location.pathname === item.href;
                    return (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        className={({ isActive }) =>
                          `block px-3 py-2 text-sm rounded transition-colors ${
                            isActive || isCurrent
                              ? 'text-sky-600 font-bold bg-white shadow-xs'
                              : 'text-slate-700 hover:text-sky-600 hover:bg-white font-medium'
                          }`
                        }
                      >
                        <div className={isCurrent ? 'font-bold' : 'font-medium'}>{item.title}</div>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>

            <NavLink
              to="/om-mig"
              className={({ isActive }) =>
                `px-4 py-3 rounded-lg text-base transition-colors flex items-center justify-between ${
                  isActive ? 'text-sky-600 font-bold' : 'text-slate-800 hover:text-sky-600 font-medium'
                }`
              }
            >
              <span>Om mig</span>
              <i className="fa-solid fa-chevron-right text-xs text-slate-300" aria-hidden="true" />
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-4 py-3 rounded-lg text-base transition-colors flex items-center justify-between ${
                  isActive ? 'text-sky-600 font-bold' : 'text-slate-800 hover:text-sky-600 font-medium'
                }`
              }
            >
              <span>Kontakt</span>
              <i className="fa-solid fa-chevron-right text-xs text-slate-300" aria-hidden="true" />
            </NavLink>

            {/* Mobile Contact Quick Actions */}
            <div className="pt-4 mt-2 space-y-3">
              <a
                href="tel:69306445"
                className="w-full py-3 px-4 bg-sky-600 text-white font-semibold rounded-lg flex items-center justify-center text-sm shadow-sm"
              >
                <span className="whitespace-nowrap">Ring på 69 30 64 45</span>
              </a>

              <a
                href="mailto:mahvidevareservice@gmail.com"
                className="w-full py-2.5 px-4 bg-slate-100 text-slate-700 font-semibold rounded-lg flex items-center justify-center text-xs hover:bg-slate-200 transition-colors whitespace-nowrap"
              >
                <span className="whitespace-nowrap">mahvidevareservice@gmail.com</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Navbar;
